import Button from "@app/components/button";
import CustomAsyncSelect from "@app/components/custom-async-select";
import { PromiseOptionsCallback } from "@app/components/custom-async-select/custom-async-select.component";
import CustomReactSelect from "@app/components/custom-react-select";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import Textarea from "@app/components/textarea";
import { addToast } from "@app/components/toast/toast.service";
import {
  RoutePath,
  SystemMessage,
  BUCKET_URL_KEY,
  MAX_SIZE_UPLOAD,
} from "@app/constants";
import AffiliateCampaignService from "@app/services/http/affiliate-campaign.service";
import BookSegmentService from "@app/services/http/book-segment.service";
import UploadMediaService from "@app/services/http/upload-media.service";
import {
  CreateAffiliateCampaign,
  CampaignType,
  CampaignTypeOptions,
} from "@app/types";
import { createAffiliateCampaignValidationSchema } from "@app/validations/affiliate-campaign.validation";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import StorageService from "@core/services/storage/storage.service";
import { Images } from "@assets/images";
import { Form, Formik, FormikContextType, useFormikContext } from "formik";
import { createRef, useCallback, useState, useRef, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";

interface CreateAffiliateCampaignForm {
  type: number;
  book: { label: string; value: number } | null;
  content: string;
  kas?: number;
  vipTime?: number;
  thumbnail?: string;
  startDate: string | null;
  endDate: string | null;
}

// Component con để xử lý upload ảnh với Formik context
const CampaignImageUpload = () => {
  const formik = useFormikContext<CreateAffiliateCampaignForm>();
  const [campaignImageFile, setCampaignImageFile] = useState<File | null>(null);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const s3Url = StorageService.getObject(BUCKET_URL_KEY);

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) {
      setCampaignImageFile(null);
      formik.setFieldValue("thumbnail", "");

      return;
    }

    const file = event.target.files[0];

    if (file.size > MAX_SIZE_UPLOAD) {
      addToast({ text: SystemMessage.MAX_SIZE_UPLOAD, status: "inValid" });
      setCampaignImageFile(null);
      formik.setFieldValue("thumbnail", "");

      return;
    }

    setCampaignImageFile(file);

    // Upload the file immediately
    UploadMediaService.upload({ medias: [file] })
      .then((response) => {
        if (response.data && response.data.items[0]) {
          const uploadedImageUrl = response.data.items[0].url;

          formik.setFieldValue("thumbnail", uploadedImageUrl);
          addToast({ text: "Upload ảnh thành công!" });
        }
      })
      .catch(() => {
        addToast({ text: "Lỗi khi upload ảnh", status: "inValid" });
        setCampaignImageFile(null);
        formik.setFieldValue("thumbnail", "");
      });
  };

  return (
    <div className="mt-7">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Ảnh Campaign <span className="text-red-500">*</span>
      </label>

      {/* Image Preview */}
      {(campaignImageFile || formik.values.thumbnail) && (
        <div className="mb-4">
          <img
            src={
              campaignImageFile
                ? URL.createObjectURL(campaignImageFile)
                : s3Url + (formik.values.thumbnail || "")
            }
            alt="Preview"
            className="w-32 h-32 object-cover rounded-lg border border-gray-200"
          />
        </div>
      )}

      <label
        className={`rounded-lg px-4 py-2 h-[52px] cursor-pointer flex items-center justify-between ${
          formik.errors.thumbnail && formik.touched.thumbnail
            ? "bg-red-50 border-2 border-red-500"
            : "bg-[rgba(239,241,249,0.6)]"
        }`}
      >
        <input
          ref={inputFileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
        />
        <div className="text-[#ABAFB1] text-base truncate mr-3">
          {campaignImageFile?.name ||
            formik.values.thumbnail ||
            "⚠️ Vui lòng upload ảnh campaign (BẮT BUỘC)"}
        </div>
        <img src={Images.UploadIcon.default} alt="upload" className="w-6 h-6" />
      </label>

      {/* Error message - Always show if there's an error */}
      {formik.errors.thumbnail && (
        <div className="text-red-500 text-sm mt-2 font-semibold">
          ⚠️ {formik.errors.thumbnail} - Bạn PHẢI UPLOAD ẢNH trước khi tạo
          campaign!
        </div>
      )}

      {/* Success message when uploaded */}
      {formik.values.thumbnail && (
        <div className="text-green-600 text-sm mt-2 font-semibold">
          ✓ Đã upload ảnh thành công!
        </div>
      )}
    </div>
  );
};

const CreateAffiliateCampaignPage = () => {
  const { abortController } = useCancellationRequest();
  const navigate = useNavigate();
  const [selectedCampaignType, setSelectedCampaignType] = useState<number>(
    CampaignType.GIFT_BOOK
  );

  const formRef = createRef<FormikContextType<CreateAffiliateCampaignForm>>();

  const onSubmit = (values: CreateAffiliateCampaignForm) => {
    const affiliateCampaign: CreateAffiliateCampaign = {
      type: values.type,
      content: values.content,
      startDate: values.startDate || null,
      endDate: values.endDate || null,
    };

    // Add type-specific fields
    if (values.type === CampaignType.GIFT_BOOK && values.book) {
      affiliateCampaign.bookId = values.book.value;
    } else if (values.type === CampaignType.GIFT_KAS) {
      affiliateCampaign.kas = values.kas;
      affiliateCampaign.thumbnail = values.thumbnail;
    } else if (values.type === CampaignType.GIFT_VIP) {
      affiliateCampaign.vipTime = values.vipTime;
      affiliateCampaign.thumbnail = values.thumbnail;
    }

    AffiliateCampaignService.create(affiliateCampaign)
      .then(() => {
        addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
        navigate(`/${RoutePath.AFFILIATE_CAMPAIGN_MANAGEMENT}`);
      })
      .catch(() => {
        addToast({ text: "Có lỗi xảy ra khi tạo campaign", status: "inValid" });
      });
  };

  const handleCampaignTypeChange = (value: number) => {
    setSelectedCampaignType(value);

    if (formRef.current) {
      formRef.current.setFieldValue("type", value);
      // Clear type-specific fields when changing type
      formRef.current.setFieldValue("book", null);
      formRef.current.setFieldValue("kas", undefined);
      formRef.current.setFieldValue("vipTime", undefined);

      // Only clear thumbnail if switching away from GIFT_KAS or GIFT_VIP
      if (value !== CampaignType.GIFT_KAS && value !== CampaignType.GIFT_VIP) {
        formRef.current.setFieldValue("thumbnail", "");
      }
    }
  };

  const getBooksOptions = useCallback(
    (inputValue: string, callback: PromiseOptionsCallback) => {
      // Use admin dashboard book-dropdown API to fetch retail books (bookOption=3)
      BookSegmentService.getBookDropdown(
        inputValue.trim(),
        abortController.signal
      ).then((res) => {
        callback(res.data.items);
      });
    },
    [abortController.signal]
  );

  const gotoListAffiliateCampaign = () => {
    navigate(`/${RoutePath.AFFILIATE_CAMPAIGN_MANAGEMENT}`);
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center ">
        <div className="text-[#343C6A]">Danh sách campaign / Tạo mới</div>
      </div>

      <Formik
        innerRef={formRef}
        initialValues={{
          type: CampaignType.GIFT_BOOK,
          book: null,
          content: "",
          kas: undefined,
          vipTime: undefined,
          thumbnail: "",
          startDate: "",
          endDate: "",
        }}
        onSubmit={onSubmit}
        validationSchema={createAffiliateCampaignValidationSchema}
        validateOnChange
        validateOnBlur
      >
        <Form>
          <div className="mt-6 rounded-xl bg-white py-6 px-5">
            <div className="w-[100%] max-w-[700px]">
              {/* Campaign Type Dropdown */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loại Campaign
                </label>
                <CustomReactSelect
                  options={CampaignTypeOptions}
                  value={CampaignTypeOptions.find(
                    (opt) => opt.value === selectedCampaignType
                  )}
                  onChange={(value) =>
                    handleCampaignTypeChange(value?.value as number)
                  }
                  placeholder="Chọn loại campaign"
                  isClearable={false}
                />
              </div>

              {/* Book Selection - Only for GIFT_BOOK */}
              {selectedCampaignType === CampaignType.GIFT_BOOK && (
                <FormControl name="book">
                  <CustomAsyncSelect
                    className="mt-7 rounded-lg"
                    promiseOptions={getBooksOptions}
                    placeholder="Chọn sách"
                    styles={{
                      stylesControl: {
                        minHeight: "52px",
                      },
                    }}
                  />
                </FormControl>
              )}

              {/* Kas Field - Only for GIFT_KAS */}
              {selectedCampaignType === CampaignType.GIFT_KAS && (
                <FormControl name="kas">
                  <Input className="mt-7" type="number" placeholder="Số kas" />
                </FormControl>
              )}

              {/* VIP Time Field - Only for GIFT_VIP */}
              {selectedCampaignType === CampaignType.GIFT_VIP && (
                <FormControl name="vipTime">
                  <Input
                    className="mt-7"
                    type="number"
                    placeholder="Thời gian VIP (giây)"
                  />
                </FormControl>
              )}

              {/* Campaign Image - For GIFT_KAS and GIFT_VIP */}
              {(selectedCampaignType === CampaignType.GIFT_KAS ||
                selectedCampaignType === CampaignType.GIFT_VIP) && (
                <CampaignImageUpload />
              )}

              {/* Content Field - Always visible */}
              <FormControl name="content">
                <Textarea
                  className="mt-7"
                  width="100%"
                  placeholder="Nội dung campaign"
                  rows={4}
                />
              </FormControl>

              {/* Date Fields - Always visible */}
              <FormControl name="startDate">
                <Input
                  className="mt-7"
                  type="date"
                  placeholder="Ngày bắt đầu"
                />
              </FormControl>
              <FormControl name="endDate">
                <Input
                  className="mt-7"
                  type="date"
                  placeholder="Ngày kết thúc"
                />
              </FormControl>
            </div>
          </div>
          <div className="mt-6 flex justify-left gap-4">
            <Button
              type="submit"
              size="xs"
              label={"Tạo"}
              width={180}
              className="!h-[51px] !leading-[51px]"
              labelClassName="text-xl"
              onClick={() => {
                // Mark thumbnail as touched if it's required for this type
                if (
                  formRef.current &&
                  (formRef.current.values.type === CampaignType.GIFT_KAS ||
                    formRef.current.values.type === CampaignType.GIFT_VIP)
                ) {
                  formRef.current.setFieldTouched("thumbnail", true);
                }
              }}
            />
            <Button
              size="xs"
              label="Hủy"
              theme="secondary"
              width={180}
              className="!h-[51px] !leading-[51px]"
              labelClassName="text-xl"
              onClick={gotoListAffiliateCampaign}
            />
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export default CreateAffiliateCampaignPage;
